
import React, { useState } from 'react';
import { TrendingUp, Users, DollarSign, Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export const IncomeCalculator = () => {
  const [memberCount, setMemberCount] = useState<number>(0);
  
  const baseIncomePerMember = 100; // Fixed at $100 per member per month
  const bonusPerMember = 20; // $20 bonus per member per month
  
  const totalBaseIncome = memberCount * baseIncomePerMember;
  const totalBonus = memberCount * bonusPerMember;
  const totalIncome = totalBaseIncome + totalBonus;

  return (
    <Card className="bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-500/30 backdrop-blur-lg">
      <CardContent className="p-8">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-white mb-2">
            TÍNH TOÁN THU NHẬP TƯƠNG LAI
          </h3>
          <p className="text-emerald-300 text-sm leading-relaxed">
            Xem bạn sẽ kiếm được bao nhiều với cộng đồng của mình
          </p>
        </div>
        
        <div className="space-y-6">
          <div>
            <div className="flex items-center mb-2">
              <label className="block text-sm font-semibold text-white">
                Số lượng member trong cộng đồng của bạn
              </label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-4 h-4 text-blue-400 ml-2 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="max-w-xs">
                      <p className="text-sm">
                        <strong>Cách tính thu nhập:</strong><br/>
                        • Thu nhập cơ bản: $100/member/tháng<br/>
                        • Bonus hoa hồng: $20/member/tháng<br/>
                        • Tổng: $120/member/tháng
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input
              type="number"
              placeholder="Ví dụ: 100 member"
              value={memberCount || ''}
              onChange={(e) => setMemberCount(Number(e.target.value))}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/60 text-lg font-semibold h-12"
            />
            {memberCount > 0 && (
              <p className="text-xs text-blue-300 mt-2 font-medium">
                💡 Với {memberCount} member, bạn có thể kiếm được ${totalIncome.toLocaleString()}/tháng
              </p>
            )}
          </div>
          
          {memberCount > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in">
              <div className="bg-gradient-to-br from-blue-600/30 to-purple-600/30 rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
                <Users className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white mb-1">
                  ${totalBaseIncome.toLocaleString()}
                </div>
                <div className="text-blue-300 font-semibold text-sm">Thu nhập từ member</div>
                <div className="text-xs text-blue-200 mt-1">
                  ${baseIncomePerMember}/member/tháng
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-emerald-600/30 to-green-600/30 rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
                <DollarSign className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white mb-1">
                  +${totalBonus.toLocaleString()}
                </div>
                <div className="text-emerald-300 font-semibold text-sm">Bonus hoa hồng</div>
                <div className="text-xs text-emerald-200 mt-1">
                  +${bonusPerMember}/member/tháng
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-amber-600/30 to-orange-600/30 rounded-xl p-6 text-center hover:scale-105 transition-all duration-300">
                <TrendingUp className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white mb-1">
                  ${totalIncome.toLocaleString()}
                </div>
                <div className="text-amber-300 font-semibold text-sm">Tổng thu nhập</div>
                <div className="text-xs text-amber-200 mt-1">
                  ${baseIncomePerMember + bonusPerMember}/member/tháng
                </div>
              </div>
            </div>
          )}
          
          {memberCount > 0 && (
            <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-xl p-6 mt-6">
              <h4 className="text-lg font-bold text-white mb-3 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2" />
                Chi tiết thu nhập
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div className="flex justify-between text-gray-300">
                    <span>Số member:</span>
                    <span className="font-semibold text-white">{memberCount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-300">
                    <span>Thu nhập/member:</span>
                    <span className="font-semibold text-blue-400">${baseIncomePerMember}</span>
                  </div>
                  <div className="flex justify-between text-gray-300">
                    <span>Bonus/member:</span>
                    <span className="font-semibold text-emerald-400">+${bonusPerMember}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-gray-300">
                    <span>Thu nhập cơ bản:</span>
                    <span className="font-semibold text-blue-400">${totalBaseIncome.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-300">
                    <span>Tổng bonus:</span>
                    <span className="font-semibold text-emerald-400">${totalBonus.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-white font-bold border-t border-white/20 pt-2">
                    <span>Tổng cộng/tháng:</span>
                    <span className="text-amber-400">${totalIncome.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
